import React from 'react';
import { Link } from 'react-router-dom';
import { Sparkles, Sun, Smartphone, Settings, Star, CheckCircle, ArrowRight, Shield, ShieldCheck, Palette } from 'lucide-react';
import { services, testimonials, businessInfo } from '../mockData';

const iconMap = {
  'Sparkles': Sparkles,
  'Sun': Sun,
  'Smartphone': Smartphone,
  'Settings': Settings,
  'Shield': Shield,
  'ShieldCheck': ShieldCheck,
  'Palette': Palette
};

const HomePage = () => {
  return (
    <div className="homepage">
      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-overlay"></div>
        <div className="container hero-content">
          <h1 className="hero-title">
            Premium Auto Services<br />
            <span className="hero-title-accent">That You Can Trust</span>
          </h1>
          <p className="hero-subtitle">
            Expert auto detailing, window tinting, CarPlay installation, and vehicle upgrades 
            serving Kitchener, Waterloo, Cambridge, and Guelph
          </p>
          <div className="hero-cta">
            <Link to="/booking" className="btn-primary btn-large">
              Book Appointment Online
              <ArrowRight size={20} />
            </Link>
            <Link to="/contact" className="btn-secondary btn-large">
              Request Quote
            </Link>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="about-section">
        <div className="container">
          <div className="about-content">
            <div className="about-text">
              <h2 className="section-title">
                Why Choose Prime Auto Logix?
              </h2>
              <p className="section-description">
                We're your local automotive care specialists in the Waterloo Region. 
                With years of experience and a commitment to excellence, we bring your 
                vehicle back to showroom condition and enhance it with the latest technology.
              </p>
              <div className="about-features">
                <div className="about-feature">
                  <CheckCircle className="feature-icon" />
                  <div>
                    <h3>Expert Technicians</h3>
                    <p>Certified professionals with extensive training and experience</p>
                  </div>
                </div>
                <div className="about-feature">
                  <CheckCircle className="feature-icon" />
                  <div>
                    <h3>Premium Products</h3>
                    <p>We use only the highest quality products and equipment</p>
                  </div>
                </div>
                <div className="about-feature">
                  <CheckCircle className="feature-icon" />
                  <div>
                    <h3>Satisfaction Guaranteed</h3>
                    <p>Your satisfaction is our priority, backed by our warranty</p>
                  </div>
                </div>
                <div className="about-feature">
                  <CheckCircle className="feature-icon" />
                  <div>
                    <h3>Local & Trusted</h3>
                    <p>Proudly serving the Waterloo Region community</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="about-image">
              <img 
                src="https://images.unsplash.com/photo-1647844870489-1966c5000382" 
                alt="Professional automotive service"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="services-section">
        <div className="container">
          <h2 className="section-title text-center">Our Services</h2>
          <p className="section-description text-center">
            Comprehensive automotive care and enhancement services
          </p>
          <div className="services-grid">
            {services.map((service) => {
              const IconComponent = iconMap[service.icon];
              return (
                <div key={service.id} className="service-card">
                  <div className="service-icon">
                    <IconComponent size={32} />
                  </div>
                  <h3 className="service-title">{service.name}</h3>
                  <p className="service-description">{service.shortDescription}</p>
                  <Link to={`/services#${service.slug}`} className="service-link">
                    Learn More
                    <ArrowRight size={16} />
                  </Link>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="testimonials-section">
        <div className="container">
          <h2 className="section-title text-center">What Our Customers Say</h2>
          <p className="section-description text-center">
            Don't just take our word for it – hear from satisfied customers across the Waterloo Region
          </p>
          <div className="testimonials-grid">
            {testimonials.slice(0, 3).map((testimonial) => (
              <div key={testimonial.id} className="testimonial-card">
                <div className="testimonial-rating">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} size={18} fill="currentColor" />
                  ))}
                </div>
                <p className="testimonial-text">"{testimonial.text}"</p>
                <div className="testimonial-author">
                  <div>
                    <p className="author-name">{testimonial.name}</p>
                    <p className="author-location">{testimonial.location}</p>
                  </div>
                  <span className="testimonial-service">{testimonial.service}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="container">
          <div className="cta-content">
            <h2 className="cta-title">Ready to Transform Your Vehicle?</h2>
            <p className="cta-description">
              Get in touch today for a free quote or to book your appointment. 
              We're here to help via phone, email, or WhatsApp!
            </p>
            <div className="cta-buttons">
              <Link to="/booking" className="btn-primary btn-large">
                Book Appointment Now
              </Link>
              <a 
                href={`https://wa.me/1${businessInfo.whatsapp}?text=${encodeURIComponent("Hi! I'd like to book a service.")}`}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-secondary btn-large"
              >
                WhatsApp Us
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
